import { defineStore } from 'pinia';
import notificationWrapper from '@/utils/helpers/notificationWrapper';
import offersHelper from '@/utils/helpers/offers';
import { useProjectStore } from './projectStore';
import { useDemandStore } from './demandStore';
import type { MoverTag, Offer, Option, PaymentData, OfferState } from '@/types/Offers';

export const useOfferStore = defineStore('offer', {
  state: (): OfferState => ({
    offers: [],
    offersLoading: false,
    offer: null,
    offerLoading: false,
    optionsLoading: false,
    moversTags: [],
    moversTagsLoading: false,
  }),

  getters: {
    selectedOffer: (state) => offersHelper.getFinishedOffer(state.offers),
  },

  actions: {
    async getOffers(isDisplayMoverPhotos: boolean) {
      this.offersLoading = true;
      const projectStore = useProjectStore();
      return window.appConnector.getOffers(projectStore.projectIdSelected, isDisplayMoverPhotos)
        .then(async (data: Offer[]) => {
          if (data) {
            this.setOffers(data);
            this.offersLoading = false;
          }
        }).catch((error: Error & { isAuthenticationError?: boolean }) => {
          this.offersLoading = false;
          notificationWrapper('error', error.isAuthenticationError
            ? 'NOT_LOGGED'
            : 'DEFAULT');
          throw error;
        });
    },
    async getOffer({ token, id, isDisplayMoverPhotos }: { token: string, id: string, isDisplayMoverPhotos: boolean }) {
      this.offerLoading = true;
      const projectStore = useProjectStore();
      return window.appConnector.getOffer(token, id, isDisplayMoverPhotos)
        .then((data: Offer) => {
          this.offerLoading = false;
          if (data && projectStore.projectSelected) {
            this.offer = offersHelper.formatOffer(
              data,
              projectStore.projectSelected,
            );
            return this.offer;
          }
          return false;
        }).catch((error: { isAuthenticationError: boolean }) => {
          notificationWrapper('error', error.isAuthenticationError
            ? 'NOT_LOGGED'
            : 'DEFAULT');
          this.offerLoading = false;
          throw error;
        });
    },
    async preBookOffer(offer: Offer) {
      this.offersLoading = true;
      const projectStore = useProjectStore();
      return window.appConnector.preBookOffer(projectStore.projectIdSelected, offer)
        .then((data: Offer) => {
          if (data) {
            this.updateOffer(data);
            this.offersLoading = false;
          }
        }).catch((error: { isAuthenticationError: boolean, data: { error: string } }) => {
          notificationWrapper('error', error.isAuthenticationError
            ? 'NOT_LOGGED'
            : 'DEFAULT');
          this.offersLoading = false;
          if (error.data && ['STRIPE_REGISTER_FAILED', 'ID_MISSING']
            .findIndex((msg) => error.data.error === msg) !== -1) {
            return;
          }
          throw error;
        });
    },
    async submitWireTransfer(paymentData: PaymentData) {
      this.offersLoading = true;
      const projectStore = useProjectStore();
      return window.appConnector.confirmPaymentOffer(projectStore.projectIdSelected, paymentData)
        .then((data: Offer) => {
          if (data) {
            this.updateOffer(data);
            notificationWrapper('success', 'CONFIRM_WIRE_TRANSFER_SUBMITTED');
            this.offersLoading = false;
          }
          return data;
        }).catch((error: { isAuthenticationError: boolean }) => {
          this.offersLoading = false;
          notificationWrapper('error', error.isAuthenticationError
            ? 'NOT_LOGGED'
            : 'DEFAULT');
          throw error;
        });
    },
    async confirmPaymentOffer({ offer, message }: { offer: Offer, message: string }) {
      this.offersLoading = true;
      const projectStore = useProjectStore();
      return window.appConnector.confirmPaymentOffer(projectStore.projectIdSelected, offer)
        .then((data: Offer) => {
          if (data) {
            this.updateOffer(data);
            notificationWrapper('success', message || 'CONFIRM_PAYMENT_OFFER');
            this.offersLoading = false;
          }
          return data;
        }).catch((error: { isAuthenticationError: boolean, status: number, data: { error_code: string } }) => {
          this.offersLoading = false;
          // PAYMENT REFUSED - INDIVIDUAL CASE
          if (error && error.status === 402 && error.data) {
            const { error_code: errorCode } = error.data;
            notificationWrapper('stripe', errorCode);
          } else {
            notificationWrapper('error', error.isAuthenticationError
              ? 'NOT_LOGGED'
              : 'DEFAULT');
          }
          throw error;
        });
    },
    async bookOffer(offer: Offer) {
      this.offersLoading = true;
      const projectStore = useProjectStore();
      const demandStore = useDemandStore();
      return window.appConnector.bookOffer(projectStore.projectIdSelected, offer)
        .then((data: { offer: Offer }) => {
          if (data) {
            this.updateOffer(data.offer);
            demandStore.getDemand();
            this.offersLoading = false;
          }
          return data;
        }).catch((error: { isAuthenticationError: boolean, status: number, data: { error: string } }) => {
          console.error(error);
          if (!error.data || error.status !== 402) {
            notificationWrapper('error', error.isAuthenticationError
              ? 'NOT_LOGGED'
              : 'DEFAULT');
          }
          this.offersLoading = false;
          if (error.data && ['ID_MISSING']
            .findIndex((msg) => error.data.error === msg) !== -1) {
            return;
          }
          throw error;
        });
    },
    async chooseOffer(offer: Offer) {
      this.offersLoading = true;
      const projectStore = useProjectStore();
      const demandStore = useDemandStore();
      return window.appConnector.bookOffer(projectStore.projectIdSelected, offer)
        .then((data: { offer: Offer }) => {
          if (data) {
            this.updateOffer(data.offer);
            demandStore.getDemand();
            this.offersLoading = false;
          }
        }).catch((error: { isAuthenticationError: boolean, data: { error: string } }) => {
          notificationWrapper('error', error.isAuthenticationError
            ? 'NOT_LOGGED'
            : 'DEFAULT');
          this.offersLoading = false;
          if (error.data && ['ID_MISSING']
            .findIndex((msg) => error.data.error === msg) !== -1) {
            return;
          }
          throw error;
        });
    },
    async postOptions(options: Option[]) {
      this.optionsLoading = true;
      const projectStore = useProjectStore();
      return window.appConnector.postOptions(projectStore.projectIdSelected, options)
        .then(() => {
          this.optionsLoading = false;
        }).catch((error: { isAuthenticationError: boolean }) => {
          notificationWrapper('error', error.isAuthenticationError
            ? 'NOT_LOGGED'
            : 'DEFAULT');
          this.optionsLoading = false;
          throw error;
        });
    },
    async getMoversTags() {
      this.moversTagsLoading = true;
      return window.moverappConnector.getTags()
        .then(({ data }: { data: MoverTag[] }) => {
          if (data) {
            this.moversTags = data;
            this.moversTagsLoading = false;
          }
        }).catch((error: { isAuthenticationError: boolean }) => {
          notificationWrapper('error', 'DEFAULT');
          this.moversTagsLoading = false;
          throw error;
        });
    },
    async setOffers(offers: Offer[]) {
      const projectStore = useProjectStore();
      this.offers = projectStore.projectSelected && offersHelper.formatOffers(offers, projectStore.projectSelected) || [];
    },
    async updateOffer(offer: Offer) {
      const index = this.offers.findIndex((stateOffer) => stateOffer.id === offer.id);
      if (index !== -1) {
        this.offers[index] = offer;
      }
    },
    async updateOption({ offerId, optionId }: { offerId: string, optionId: string }) {
      const index = this.offers.findIndex(({ id }) => offerId === id);
      if (index !== -1) {
        const offer: Offer = this.offers[index];
        const optionIndex = offer.options.findIndex((option: Option) => option.id === optionId);
        if (optionIndex !== -1) {
          const wasChosen = offer.options[optionIndex].is_chosen;
          offer.options[optionIndex].is_chosen = !wasChosen;
          // Les formules proposées en option sont mutuellement exclusives :
          // sélectionner une formule désélectionne les autres formules de la même offre.
          if (!wasChosen && offer.options[optionIndex].category === 'formule') {
            offer.options.forEach((option: Option, i: number) => {
              if (i !== optionIndex && option.category === 'formule') {
                option.is_chosen = false;
              }
            });
          }
          this.offers[index].options = offer.options;
          if (this.offer?.id === offerId) {
            this.offer.options = offer.options;
          }
        }
      }
    },
  },
});
